import React, { createContext, useContext, useState, useEffect } from 'react';
import API from '../services/api';
import { isTokenExpired, getAccessToken } from '../utils/auth';
import { refreshToken, logout } from '../services/authService';

const UserContext = createContext();

export const UserProvider = ({ children }) => {
  const [userProfile, setUserProfile] = useState(null);
  const [userLoading, setUserLoading] = useState(true);

  const fetchProfile = async () => {
    const token = getAccessToken();

    if (!token) {
      setUserProfile(null);
      setUserLoading(false);
      return null;
    }

    try {
      if (isTokenExpired(token)) {
        const newToken = await refreshToken();
        if (!newToken) {
          setUserProfile(null);
          setUserLoading(false);
          await logout();
          return null;
        }
      }

      setUserLoading(true);
      const response = await API.get('/user-profile/');
      setUserProfile(response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching user profile:', error);
      await logout();
      return null;
    } finally {
      setUserLoading(false);
    }
  };

  useEffect(() => {
    fetchProfile();
  }, []);

  const handleLogout = async () => {
    try {
      await logout();
    } finally {
      setUserProfile(null);
    }
  };
  
  // New function to update tokens after password change
  const updateToken = (accessToken, refreshToken) => {
    localStorage.setItem('access_token', accessToken);
    localStorage.setItem('refresh_token', refreshToken);
    // Update axios default headers if needed
    if (API.defaults && API.defaults.headers) {
      API.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
    }
  };

  return (
    <UserContext.Provider value={{
      userProfile,
      userLoading,
      logout: handleLogout,
      fetchProfile,
      updateToken
    }}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};